import { BreadcrumbHeading, Spinner, Typography } from '@weareopenr/component-library';
import styles from './ProductPublicationStatus.module.scss';
import { useMemo } from 'react';
import { formatDate } from '../../helpers/formatDate/formatDate';
import { formatTime } from '../../helpers/formatTime/formatTime';
import dayjs from 'dayjs';
import { Routes } from 'src/routes';
import { useEvent } from 'src/hooks/useEvent';
import { IEvent, EVENT_TYPES, ENTITY_TYPES, EVENT_STATUSES, NotificationStatus } from 'src/api/getEvents';
import { useParams, useLocation } from 'react-router-dom';
import { capitalise } from 'src/helpers/capitalise';
import { formatStatus } from 'src/helpers/getPublicationStatus/getPublicationStatus';
import { pluralize } from 'src/helpers/pluralize/pluralize';
import { getPublishTypeLabel } from 'src/helpers/getPublishTypeLabel/getPublishTypeLabel';
import { PublishType } from 'src/components/ProductPublishModal/types';
import { StatusIcon } from 'src/components/StatusIcon';
import { getMenuVenueCounts } from 'src/utils/schedules/getMenuVenueCounts';
import { InfoTooltip, InfoTooltipType } from 'src/components/InfoTooltip/InfoTooltip';

interface ExternalSystemGroup {
  name: string;
  productEvent?: IEvent;
  pricingEvent?: IEvent;
  menuEvent?: IEvent;
  timetableEvent?: IEvent;
}

const formatDateTime = (date: Date) => `${formatDate(date)}, ${formatTime(date)}`;

const isExternalSystemEvent = (child: IEvent) =>
  (child.event_type === EVENT_TYPES.PUBLISH_PRICING ||
    child.event_type === EVENT_TYPES.PUBLISH_PRODUCT ||
    child.event_type === EVENT_TYPES.PUBLISH_MENU ||
    child.event_type === EVENT_TYPES.PUBLISH_TIMETABLE ||
    child.event_type === EVENT_TYPES.PUBLISH_MENU_SET) &&
  child.entity_type === ENTITY_TYPES.EXTERNAL_SYSTEM;

const MenuChannelSection = ({ channelName, menuEvent }: { channelName: string; menuEvent: IEvent }) => {
  const menuVenues = useMemo(
    () =>
      (menuEvent.child_events ?? []).filter(
        (e) => e.entity_type === ENTITY_TYPES.VENUE && e.status !== EVENT_STATUSES.UNTRACKED,
      ),
    [menuEvent],
  );
  const venueCounts = useMemo(() => getMenuVenueCounts(menuVenues), [menuVenues]);
  const isChannelFailure = menuEvent.status === EVENT_STATUSES.FAILURE;

  return (
    <>
      <div className={styles['item']}>
        <StatusIcon status={menuEvent.status} animate={menuEvent.status === NotificationStatus.PENDING} />
        <div>
          <Typography size="small" weight="bold">
            {channelName}
          </Typography>
          <Typography size="small" className={styles['menu-channel-summary']}>
            {`${channelName} - ${menuVenues.length} ${menuVenues.length === 1 ? 'venue' : 'venues'}`}
          </Typography>
          <Typography size="small">
            {`Success - ${venueCounts.success} ${pluralize('Venue', venueCounts.success)} | Pending - ${venueCounts.pending} ${pluralize('Venue', venueCounts.pending)} | Failed - ${venueCounts.failure} ${pluralize('Venue', venueCounts.failure)}`}
          </Typography>
          {isChannelFailure && menuEvent.message && (
            <Typography size="small" className={styles['error-message']}>
              {menuEvent.message}
            </Typography>
          )}
        </div>
      </div>

      {menuVenues.length > 0 && (
        <div className={styles['venue-list']}>
          {menuVenues.map((venueEvent) => {
            const isFailure = venueEvent.status === EVENT_STATUSES.FAILURE;
            const statusLine = isFailure && venueEvent.message ? venueEvent.message : formatStatus(venueEvent.status);

            return (
              <div key={venueEvent.event_id} className={styles['item']}>
                <StatusIcon status={venueEvent.status} animate={venueEvent.status === NotificationStatus.PENDING} />
                <div>
                  <Typography size="small" weight="bold">
                    {venueEvent.entity_name}
                  </Typography>
                  <Typography size="small">{statusLine}</Typography>
                  <Typography size="small" className={styles['event-time']}>
                    {`Last Updated - ${formatDateTime(new Date(venueEvent.updated_at))}`}
                  </Typography>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

export const ProductPublicationStatus = () => {
  const { eventId } = useParams<{ eventId?: string }>();
  const location = useLocation();
  const { data: event, isLoading: loading } = useEvent(eventId);
  const entityNameFromHistory = (location.state as { entityName?: string | null } | null)?.entityName;

  const lastPublishedDate = useMemo(() => {
    return event?.updated_at &&
      (event?.status === NotificationStatus.SENT || event.status === NotificationStatus.PARTIAL_SUCCESS)
      ? new Date(event.updated_at)
      : null;
  }, [event]);

  const externalSystemGroups = useMemo(() => {
    if (!event?.child_events) return new Map<string, ExternalSystemGroup>();

    const groups = new Map<string, ExternalSystemGroup>();
    const eventsBySystem = new Map<string, IEvent[]>();
    event.child_events
      .filter((childEvent) => isExternalSystemEvent(childEvent) && childEvent.status !== EVENT_STATUSES.UNTRACKED)
      .forEach((childEvent) => {
        if (!childEvent.entity_id) return;

        const existing = eventsBySystem.get(childEvent.entity_id);
        if (existing) {
          existing.push(childEvent);
        } else {
          eventsBySystem.set(childEvent.entity_id, [childEvent]);
        }
      });

    eventsBySystem.forEach((systemEvents, externalSystemId) => {
      const pricingEvent = systemEvents.find((e) => e.event_type === EVENT_TYPES.PUBLISH_PRICING);
      const productEvent = systemEvents.find((e) => e.event_type === EVENT_TYPES.PUBLISH_PRODUCT);
      // A Menu Set reaches each channel as one ordinary menu, so its channel events render as a menu's.
      const menuEvent = systemEvents.find(
        (e) => e.event_type === EVENT_TYPES.PUBLISH_MENU || e.event_type === EVENT_TYPES.PUBLISH_MENU_SET,
      );
      const timetableEvent = systemEvents.find((e) => e.event_type === EVENT_TYPES.PUBLISH_TIMETABLE);

      groups.set(externalSystemId, {
        name: systemEvents.find((e) => e.entity_name)?.entity_name ?? '',
        productEvent,
        pricingEvent,
        menuEvent,
        timetableEvent,
      });
    });

    return groups;
  }, [event]);

  const publishDetails = useMemo(() => {
    const isMenuPublish = event?.event_type === EVENT_TYPES.PUBLISH_MENU;
    const isTimetablePublish = event?.event_type === EVENT_TYPES.PUBLISH_TIMETABLE;
    const isMenuSetPublish = event?.event_type === EVENT_TYPES.PUBLISH_MENU_SET;
    const isPricingPublish =
      !isMenuPublish &&
      !isTimetablePublish &&
      !isMenuSetPublish &&
      event?.child_events?.find(isExternalSystemEvent)?.event_type === EVENT_TYPES.PUBLISH_PRICING;

    let priceSchemeEvent: IEvent | undefined;
    let publishType: string;

    switch (true) {
      case isMenuPublish:
        priceSchemeEvent = event?.child_events?.find((c) => c.entity_type === ENTITY_TYPES.PRICE_SCHEME);
        publishType = 'Menu Publish';
        break;
      case isMenuSetPublish:
        // The contract puts the PRICE_SCHEME log directly under the root, as for a menu.
        priceSchemeEvent = event?.child_events?.find((c) => c.entity_type === ENTITY_TYPES.PRICE_SCHEME);
        publishType = 'Menu Set Publish';
        break;
      case isPricingPublish:
        priceSchemeEvent = event?.child_events
          ?.flatMap((e) => e.child_events ?? [])
          .find((c) => c.entity_type === ENTITY_TYPES.PRICE_SCHEME);
        publishType = getPublishTypeLabel(PublishType.PRICING).title;
        break;
      case isTimetablePublish:
        // A timetable can resolve to several menu/price-scheme pairs, not one - there's no
        // single price scheme to summarise here the way there is for a menu/pricing publish.
        publishType = 'Menu & Pricing Timetable';
        break;
      default:
        publishType = getPublishTypeLabel(PublishType.PRODUCT).title;
    }

    const snapshotDate = event?.created_at ? dayjs(event.created_at).format('DD/MM/YY, HH:mm') : null;

    return {
      publishType,
      publishDate: event?.created_at ? dayjs(event.created_at).format('ddd, D MMM, HH:mm') : null,
      isPricingPublish,
      isMenuPublish,
      isTimetablePublish,
      isMenuSetPublish,
      priceScheme: priceSchemeEvent?.entity_name ?? null,
      entityName: event?.entity_name ?? entityNameFromHistory ?? null,
      snapshotTooltip: snapshotDate
        ? `This timetable was published using a snapshot of the menu and pricing data at ${snapshotDate}`
        : null,
    };
  }, [event, entityNameFromHistory]);

  const showsEntityName =
    publishDetails.isMenuPublish || publishDetails.isTimetablePublish || publishDetails.isMenuSetPublish;

  let subtitle: string | null = null;
  if (showsEntityName) {
    subtitle = event?.status ? `Publish status - ${formatStatus(event.status)}` : null;
  } else if (lastPublishedDate) {
    subtitle = `Last published: ${formatDateTime(lastPublishedDate)}`;
  }

  return (
    <>
      <BreadcrumbHeading
        breadcrumbs={[
          location.pathname.startsWith(Routes.PUBLISH_HISTORY)
            ? { label: 'Publish History', path: Routes.PUBLISH_HISTORY }
            : { label: 'Product Library', path: Routes.PRODUCT_LIBRARY },
          {
            label: (showsEntityName && publishDetails.entityName) || 'Publication Status',
            path: location.pathname,
          },
        ]}
        subtitle={subtitle}
      />
      {loading ? (
        <div className={styles['spinner']}>
          <Spinner size="xlarge" colour="rainbow" data-testid="spinner" />
        </div>
      ) : (
        <div className={styles['publication-status']}>
          <div className={styles['publish-details']}>
            <Typography size="xlarge" weight="semi-bold">
              Publish Details
            </Typography>
            <div className={styles['publish-details-grid']}>
              {[
                { label: 'Publish Type', value: publishDetails.publishType, tooltip: null },
                { label: 'Publish Date', value: publishDetails.publishDate ?? '—', tooltip: null },
                ...(publishDetails.isTimetablePublish
                  ? [
                      {
                        label: 'Timetable',
                        value: publishDetails.entityName ?? '—',
                        tooltip: publishDetails.snapshotTooltip,
                      },
                    ]
                  : []),
                ...(publishDetails.isPricingPublish || publishDetails.isMenuPublish || publishDetails.isMenuSetPublish
                  ? [{ label: 'Price Scheme', value: publishDetails.priceScheme ?? '—', tooltip: null }]
                  : []),
              ].map(({ label, value, tooltip }) => (
                <div key={label} className={styles['publish-detail']}>
                  <div className={styles['label-row']}>
                    <Typography weight="bold">{label}</Typography>
                    {tooltip && (
                      <span className={styles['info-icon']}>
                        <InfoTooltip type={InfoTooltipType.TIMETABLE_SNAPSHOT} text={tooltip} />
                      </span>
                    )}
                  </div>
                  <Typography>{value}</Typography>
                </div>
              ))}
            </div>
          </div>
          <div className={styles['status-container']}>
            {Array.from(externalSystemGroups.entries()).map(([externalSystemId, group]) => (
              <div key={externalSystemId} className={styles['external-system-group']}>
                <div className={styles['events']}>
                  {group.productEvent && (
                    <div className={styles['item']}>
                      <StatusIcon
                        status={group.productEvent.status}
                        animate={group.productEvent.status === NotificationStatus.PENDING}
                      />
                      <div>
                        <Typography size="small" weight="bold">
                          {group.name}
                        </Typography>
                        <Typography size="small">{capitalise(group.productEvent.status)}</Typography>
                        {group.productEvent.message && group.productEvent.status !== NotificationStatus.SCHEDULED && (
                          <Typography size="small" className={styles['error-message']}>
                            {group.productEvent.message}
                          </Typography>
                        )}
                        <Typography size="small" className={styles['event-time']}>
                          {` ${group.productEvent.status === NotificationStatus.SCHEDULED ? formatDateTime(new Date(group.productEvent.message.split('Publish scheduled for ')[1])) : formatDateTime(new Date(group.productEvent.updated_at))}`}
                        </Typography>
                      </div>
                    </div>
                  )}

                  {group.pricingEvent && (
                    <>
                      <div className={styles['item']}>
                        <StatusIcon
                          status={group.pricingEvent.status}
                          animate={group.pricingEvent.status === NotificationStatus.PENDING}
                        />
                        <div>
                          <Typography size="small" weight="bold">
                            {group.name}
                          </Typography>
                          <Typography size="small">{capitalise(group.pricingEvent.status)} </Typography>
                          {group.pricingEvent.message && group.pricingEvent.status !== NotificationStatus.SCHEDULED && (
                            <Typography size="small" className={styles['error-message']}>
                              {group.pricingEvent.message}
                            </Typography>
                          )}
                          <Typography size="small" className={styles['event-time']}>
                            {` ${group.pricingEvent.status === NotificationStatus.SCHEDULED ? formatDateTime(new Date(group.pricingEvent.message.split('Publish scheduled for ')[1])) : formatDateTime(new Date(group.pricingEvent.updated_at))}`}
                          </Typography>
                        </div>
                      </div>

                      {group.pricingEvent.child_events && group.pricingEvent.child_events.length > 0 && (
                        <div className={styles['venue-list']}>
                          {group.pricingEvent.child_events
                            .filter(
                              (e) => e.entity_type === ENTITY_TYPES.VENUE && e.status !== EVENT_STATUSES.UNTRACKED,
                            )
                            .map((venueEvent) => (
                              <div key={venueEvent.event_id} className={styles['item']}>
                                <StatusIcon
                                  status={venueEvent.status}
                                  animate={venueEvent.status === NotificationStatus.PENDING}
                                />
                                <div>
                                  <Typography size="small" weight="bold">
                                    {venueEvent.entity_name}
                                  </Typography>
                                  <Typography size="small">{capitalise(venueEvent.status)}</Typography>
                                  {venueEvent.message && (
                                    <Typography size="small" className={styles['error-message']}>
                                      {venueEvent.message}
                                    </Typography>
                                  )}
                                  <Typography size="small" className={styles['event-time']}>
                                    {`Last Updated - ${formatDateTime(new Date(venueEvent.updated_at))}`}
                                  </Typography>
                                </div>
                              </div>
                            ))}
                        </div>
                      )}
                    </>
                  )}

                  {group.menuEvent && <MenuChannelSection channelName={group.name} menuEvent={group.menuEvent} />}
                  {group.timetableEvent && (
                    <MenuChannelSection channelName={group.name} menuEvent={group.timetableEvent} />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};
